import { useRef, useState, useEffect } from 'react'
import { useGLTF, Text, Decal } from '@react-three/drei'
import { useSnapshot } from 'valtio'
import { state } from '../../../context/store'
import { fonts } from '../../fonts'
import { logoImagesLeft } from '../../textures'
import * as THREE from 'three'

const SuperBadge = ({
  modelPosition = [0, 0, 0],
  modelRotation = [0, 0, 0],
  modelScale = 0.015,
  textNamePosition = [0, 0, 1.2],
  textNameRotation = [0, 0, 0],
  textNameScale = 1,
  textNumberPosition = [0, 0, 1.2],
  textNumberRotation = [0, 0, 0],
  textNumberScale = 1,
  model,
  mode,
}) => {
  const snap = useSnapshot(state.current)
  const uploadedImage = snap.uploadedFile
  const badge = snap.choices.superBadge

  const [texture, setTexture] = useState(null)

  const textRefNumOuter = useRef()
  const textRefNumInner = useRef()

  const fontSizes = {
    small: 1.1,
    medium: 0.7,
    large: 0.5,
  }

  const nameFontOffsets = {
    draco: { x: 0, y: 0.11, z: 0.5 },
    hauser: { x: 0, y: 0, z: 0.25 },
    helvetica: { x: -0.05, y: 0, z: 0.15 },
    padaloma: { x: -0.15, y: -0.04, z: 0.35 },
  }

  const numFontOffsets = {
    draco: { x: 0, y: 0.11, z: 0.5 },
    hauser: { x: 0, y: 0, z: 0.25 },
    helvetica: { x: -0.05, y: 0, z: 0.15 },
    padaloma: { x: -0.15, y: -0.04, z: 0.35 },
  }

  const namefontKey = badge.selected.nameFont
  const numfontKey = badge.selected.numFont

  const nameOffset = nameFontOffsets[namefontKey] || {
    x: 0,
    y: 0,
    z: 0,
  }

  const numOffset = numFontOffsets[numfontKey] || {
    x: 0,
    y: 0,
    z: 0,
  }

  const nameSize = fontSizes[badge.selected.riderNameSize] || 1
  const numSize = fontSizes[badge.selected.numSize] || 1

  function getTextColor(hex) {
    hex = hex.replace('#', '')

    const r = parseInt(hex.substring(0, 2), 16)
    const g = parseInt(hex.substring(2, 4), 16)
    const b = parseInt(hex.substring(4, 6), 16)

    const luminance = 0.299 * r + 0.587 * g + 0.114 * b

    return luminance > 156 ? 'black' : 'white'
  }

  const textBG = getTextColor(badge.selected.hex)

  useEffect(() => {
    const loader = new THREE.TextureLoader()

    loader.load(uploadedImage.image ?? logoImagesLeft.none, (tex) => {
      tex.repeat.set(1, 1)
      tex.wrapS = THREE.RepeatWrapping
      tex.wrapT = THREE.RepeatWrapping
      tex.needsUpdate = true

      setTexture(tex)
    })

    return () => {
      setTexture(null)
    }
  }, [uploadedImage.image])

  const { nodes, materials } = useGLTF(model)

  // Darker / better shadow response

  useEffect(() => {
    if (!materials.MainLogoWhite) return

    materials.MainLogoWhite.roughness = 1
    materials.MainLogoWhite.metalness = 0
    materials.MainLogoWhite.aoMapIntensity = 30
    materials.MainLogoWhite.envMapIntensity = 0.3

    materials.MainLogoWhite.needsUpdate = true
  }, [materials])

  if (!badge) return null

  return (
    <group
      dispose={null}
      rotation={modelRotation}
      position={modelPosition}
      scale={modelScale}

    >
      <group>
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.Mesh.geometry}
          material={materials.MainLogoBlack}
        />

        <mesh
          castShadow
          receiveShadow
          geometry={nodes.Mesh_1.geometry}
          material={materials.MainLogoWhite}
          material-envMapIntensity={1.3}
          material-roughness={0.5}
          material-metalness={1}
        />

        <mesh
          castShadow
          receiveShadow
          geometry={nodes.Mesh_2.geometry}
          material={materials.Pattern}
        >
          {mode === 'name' && (
            <>
              {/* Outer outline */}
              <Text
                position={[
                  textNamePosition[0],
                  textNamePosition[1] + 0.002,
                  textNamePosition[2],
                ]}
                rotation={textNameRotation}
                scale={textNameScale * nameSize * 1.1}
                font={fonts[badge.selected.nameFont]}
                color={badge.selected.hex}
                outlineWidth={0.03}
                outlineColor="white"
                fontWeight={700}
                anchorX="left"
                anchorY="middle"
                renderOrder={2}
                material-polygonOffset
                material-polygonOffsetFactor={1}
                material-polygonOffsetUnits={1}
              >
                {badge.selected.riderName?.toUpperCase()}
              </Text>

              {/* Inner outline */}
              <Text
                position={textNamePosition}
                rotation={textNameRotation}
                scale={textNameScale * nameSize * 1.1}
                font={fonts[badge.selected.nameFont]}
                color={badge.selected.hex}
                fontWeight={700}
                outlineWidth={0.06}
                outlineColor={textBG === 'black' ? textBG : badge.selected.hex}
                anchorX="left"
                anchorY="middle"
                renderOrder={1}
                depthTest={false}
              >
                {badge.selected.riderName?.toUpperCase()}
              </Text>
            </>
          )}

          {mode === 'number' && (
            <>
              {/* Outer outline */}
              <Text
                ref={textRefNumOuter}
                position={[
                  textNumberPosition[0] + numOffset.x,
                  textNumberPosition[1] + numOffset.y + 0.0025,
                  textNumberPosition[2] + numOffset.z,
                ]}
                rotation={textNumberRotation}
                scale={textNumberScale * numSize}
                color={badge.selected.hex}
                font={fonts[badge.selected.numFont]}
                outlineWidth={0.03}
                outlineColor="white"
                fontWeight={700}
                anchorX="left"
                anchorY="left"
                material-polygonOffset
                material-polygonOffsetFactor={1}
                material-polygonOffsetUnits={1}
                curveRadius={-22}
                renderOrder={1}
              >
                {badge.selected.number}
              </Text>

              {/* Inner outline */}
              <Text
                ref={textRefNumInner}
                position={[
                  textNumberPosition[0] + numOffset.x,
                  textNumberPosition[1] + numOffset.y,
                  textNumberPosition[2] + numOffset.z,
                ]}
                rotation={textNumberRotation}
                scale={textNumberScale * numSize}
                color={badge.selected.hex}
                font={fonts[badge.selected.numFont]}
                fontWeight={700}
                anchorX="left"
                anchorY="left"
                outlineWidth={0.06}
                outlineColor={textBG === 'black' ? textBG : badge.selected.hex}
                material-polygonOffset
                material-polygonOffsetFactor={1}
                material-polygonOffsetUnits={1}
                curveRadius={-22}
              >
                {badge.selected.number}
              </Text>
            </>
          )}

          {mode === 'image' && texture && (
            <Decal
              rotation={[Math.PI / 2 + Math.PI, 0, 0]}
              position={[-0.5, -3.8, 0.15]}
              scale={[2, 2 / uploadedImage.ratio, 2]}
            >
              <meshStandardMaterial
                map={texture}
                roughness={1}
                metalness={0}
                polygonOffset
                polygonOffsetFactor={-1}
              />
            </Decal>
          )}
        </mesh>
      </group>
    </group>
  )
}

export default SuperBadge
